import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { getUploadUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { ArrowLeft, Printer, FileText, Loader2, Download, Mail } from "lucide-react";
import { toast } from "sonner";
import html2pdf from "html2pdf.js";

export default function InvoiceClient() {
  const { id: batchId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const printRef = useRef();
  const [loading, setLoading] = useState(true);
  const [batch, setBatch] = useState(null);
  const [product, setProduct] = useState(null);
  const [client, setClient] = useState(null);
  const [config, setConfig] = useState(null);
  const [empresaActiva, setEmpresaActiva] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [saving, setSaving] = useState(false);
  const [savingPdf, setSavingPdf] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [ivaRate, setIvaRate] = useState("10");

  useEffect(() => {
    fetchData();
  }, [batchId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [batchRes, configRes] = await Promise.all([
        api.get(`/batches/${batchId}`),
        api.get("/config")  // Obtener toda la configuración para acceder a todas las empresas
      ]);
      
      const batchData = batchRes.data;
      setBatch(batchData);
      setConfig(configRes.data);
      
      // Obtener la empresa del lote (no la activa del sistema)
      const empresas = configRes.data.empresas || [];
      let empresa = null;
      
      console.log("[InvoiceClient] Batch empresa_id:", batchData.empresa_id);
      console.log("[InvoiceClient] Empresas disponibles:", empresas.map(e => ({id: e.id, nombre: e.nombre, rgseaa: e.rgseaa})));
      
      // Prioridad 1: Buscar por empresa_id del lote
      if (batchData.empresa_id) {
        empresa = empresas.find(e => e.id === batchData.empresa_id || String(e.id) === String(batchData.empresa_id));
        console.log("[InvoiceClient] Empresa encontrada por batch.empresa_id:", empresa?.nombre);
      }
      
      // Prioridad 2: Si no tiene empresa_id, usar la empresa activa
      if (!empresa) {
        const empresaActivaId = configRes.data.empresa_activa_id;
        empresa = empresas.find(e => e.id === empresaActivaId || String(e.id) === String(empresaActivaId));
        console.log("[InvoiceClient] Empresa encontrada por empresa_activa_id:", empresa?.nombre);
      }
      
      // Prioridad 3: Primera empresa disponible
      if (!empresa && empresas.length > 0) {
        empresa = empresas[0];
        console.log("[InvoiceClient] Usando primera empresa disponible:", empresa?.nombre);
      }
      
      console.log("[InvoiceClient] Empresa seleccionada:", empresa?.nombre, "| rgseaa:", empresa?.rgseaa);
      setEmpresaActiva(empresa);
      
      // Si ya tiene factura_destino, usarla; sino mostrar preview del formato
      if (batchData.factura_destino) {
        setInvoiceNumber(batchData.factura_destino);
      } else {
        const now = new Date();
        const dateStr = now.toISOString().slice(2,10).replace(/-/g, ''); // AAMMDD
        const nextNum = empresa?.next_client_invoice_number || 1;
        setInvoiceNumber(`${dateStr}${nextNum} (sin generar)`);
      }
      
      if (batchData.producto_id) {
        try {
          const productRes = await api.get(`/products/${batchData.producto_id}`);
          setProduct(productRes.data);
        } catch (err) {}
      }
      
      if (batchData.cliente_id) {
        try {
          const clientRes = await api.get(`/clients/${batchData.cliente_id}`);
          setClient(clientRes.data);
        } catch (err) {}
      }
    } catch (error) {
      toast.error("Error al cargar los datos");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // Limpiar nombre (sin comas ni puntos)
  const cleanName = (name) => {
    return (name || "").replace(/[,\.]/g, "").trim();
  };

  // Generar nombre del archivo según fórmula:
  // Factura Cliente: AAAAMMDD "CLIENTE" NÚMEROFACTURACLIENTE LOTE CONTENEDOR/AWB
  // Si existe fecha_factura_cliente, usa esa fecha; si no, usa la fecha actual
  const generateFileName = (facturaNum) => {
    let dateStr;
    if (batch?.fecha_factura_cliente) {
      // Usar fecha_factura_cliente: formato YYYY-MM-DD -> YYYYMMDD
      dateStr = batch.fecha_factura_cliente.replace(/-/g, '');
    } else {
      // Usar fecha actual
      const now = new Date();
      dateStr = now.toISOString().slice(0,10).replace(/-/g, '');
    }
    const clienteName = cleanName(client?.nombre || batch?.cliente_nombre || "CLIENTE");
    const lote = batch?.numero_lote || "";
    const envio = batch?.contenedor || batch?.awb_aereo || "";
    return `${dateStr} ${clienteName} ${facturaNum} ${lote} ${envio}`.trim().replace(/\s+/g, ' ');
  };

  // Generar número de factura cliente (si no existe)
  const generateInvoiceNumber = async () => {
    if (batch?.factura_destino) {
      return batch.factura_destino;
    }
    
    try {
      const response = await api.post(`/batches/${batchId}/generate-client-invoice-number`);
      const newNumber = response.data.factura_destino;
      setInvoiceNumber(newNumber);
      // Actualizar batch local
      setBatch({ ...batch, factura_destino: newNumber });
      return newNumber;
    } catch (error) {
      if (error.response?.data?.already_exists) {
        return error.response.data.factura_destino;
      }
      throw error;
    }
  };

  // Guardar como PDF con nombre específico
  const handleSavePdf = async () => {
    setSavingPdf(true);
    try {
      // Primero generar número si no existe
      const facturaNum = await generateInvoiceNumber();
      const filename = generateFileName(facturaNum);
      
      const element = printRef.current;
      if (!element) {
        toast.error("Error: no se encontró el contenido de la factura");
        return;
      }

      const opt = {
        margin: 10,
        filename: `${filename}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };

      // Generar el PDF blob
      const pdfBlob = await html2pdf().set(opt).from(element).outputPdf('blob');
      
      // Descargar localmente
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filename}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      // Guardar en el servidor automáticamente (en segundo plano)
      try {
        const reader = new FileReader();
        reader.readAsDataURL(pdfBlob);
        reader.onloadend = async () => {
          try {
            const base64data = reader.result.split(',')[1]; // Quitar prefijo data:application/pdf;base64,
            const saveResponse = await api.post(`/batches/${batchId}/save-invoice-pdf`, {
              pdf_base64: base64data,
              filename: filename,
              tipo: "destino"
            });
            console.log("[SAVE_PDF] PDF guardado en servidor:", saveResponse.data);
          } catch (saveErr) {
            console.error("[SAVE_PDF] Error al guardar PDF en servidor:", saveErr);
            toast.warning("PDF descargado pero no se pudo guardar en el servidor");
          }
        };
      } catch (saveError) {
        console.warn("No se pudo guardar el PDF en el servidor:", saveError);
        toast.warning("PDF descargado pero no se pudo guardar en el servidor");
      }

      toast.success("PDF guardado correctamente");
    } catch (error) {
      console.error("Error generando PDF:", error);
      toast.error("Error al guardar el PDF");
    } finally {
      setSavingPdf(false);
    }
  };

  // Solo crear/registrar la factura (sin descargar PDF)
  const handleCreateInvoice = async () => {
    setSaving(true);
    try {
      // Primero generar número si no existe
      const facturaNum = await generateInvoiceNumber();
      
      const formData = new FormData();
      formData.append("contenedor", batch?.contenedor || "");
      formData.append("awb", batch?.awb_aereo || "");
      formData.append("bl", batch?.bl_maritimo || "");
      formData.append("numero_lote", batch?.numero_lote || "");
      formData.append("tipo_documento", "factura_cliente");
      formData.append("batch_id", batchId);
      formData.append("cliente_id", batch?.cliente_id || "");
      
      const filename = generateFileName(facturaNum);
      formData.append("nombre_archivo", `${filename}.pdf`);
      
      const emptyBlob = new Blob([''], { type: 'application/pdf' });
      formData.append("file", emptyBlob, `${filename}.pdf`);
      
      const response = await api.post("/documents/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      // Si es duplicado, mostrar mensaje informativo
      if (response.data?.status === "duplicate") {
        toast.info(`La factura ${facturaNum} ya existe para este lote`);
      } else {
        toast.success(`Factura ${facturaNum} creada correctamente`);
      }
    } catch (error) {
      console.error("[CreateInvoice] Error:", error.response?.data || error.message);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || "Error al crear la factura";
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  // Enviar factura por email al cliente
  const handleSendEmail = async () => {
    setSendingEmail(true);
    try {
      // Primero generar número si no existe
      const facturaNum = await generateInvoiceNumber();
      const filename = generateFileName(facturaNum);
      
      const element = printRef.current;
      if (!element) {
        toast.error("Error: no se encontró el contenido de la factura");
        return;
      }

      const opt = {
        margin: 10,
        filename: `${filename}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };

      // Generar el PDF como base64
      const pdfBase64 = await html2pdf().set(opt).from(element).outputPdf('datauristring');
      // Remover el prefijo "data:application/pdf;base64,"
      const base64Data = pdfBase64.split(',')[1];
      
      // Enviar al backend
      const response = await api.post(`/batches/${batchId}/send-invoice-email`, {
        pdf_base64: base64Data,
        filename: `${filename}.pdf`,
        iva_rate: parseInt(ivaRate)
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.warning(response.data.message || "No se pudo enviar el email");
      }
    } catch (error) {
      console.error("Error enviando email:", error);
      toast.error(error.response?.data?.detail || "Error al enviar el email");
    } finally {
      setSendingEmail(false);
    }
  };

  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined) return "0,00";
    return Number(num).toLocaleString("es-ES", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  };

  // Formatear fecha SIN conversión de zona horaria
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const datePart = dateStr.split('T')[0];
    const parts = datePart.split('-');
    if (parts.length === 3) {
      const [year, month, day] = parts;
      return `${day}/${month}/${year}`;
    }
    return dateStr;
  };

  // Formatear dirección del cliente en líneas
  const formatClientAddress = () => {
    if (!client) return [];
    const lines = [];
    
    // Línea 1: Dirección principal
    if (client.direccion) {
      lines.push(client.direccion);
    }
    
    // Línea 2: Código postal, ciudad, provincia
    let locationParts = [];
    if (client.codigo_postal) locationParts.push(client.codigo_postal);
    if (client.ciudad) locationParts.push(client.ciudad);
    if (client.provincia && client.provincia !== client.ciudad) {
      locationParts.push(`(${client.provincia})`);
    }
    if (locationParts.length > 0) {
      lines.push(locationParts.join(" "));
    }
    
    // Línea 3: País
    if (client.pais) {
      lines.push(client.pais);
    }
    
    return lines;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
      </div>
    );
  }

  if (!batch) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <p className="text-slate-500">Lote no encontrado</p>
          <Link to="/batches" className="text-sky-600 hover:underline mt-2 inline-block">
            Volver a lotes
          </Link>
        </div>
      </div>
    );
  }

  const precioUnitario = batch.precio_venta || 0;
  // Usar kilos_facturables + destare_cliente para la factura cliente
  const kilosFacturables = batch.kilos_facturables || batch.peso_neto || 0;
  const destareCliente = batch.destare_cliente || 0;
  const unidades = kilosFacturables + destareCliente;
  
  // Calcular descuento
  let descuentoPorcentaje = 0;
  let descuentoEurosUnitario = 0;
  let precioConDescuento = precioUnitario;
  
  if (batch.descuento_porcentaje && batch.descuento_porcentaje > 0) {
    descuentoPorcentaje = parseFloat(batch.descuento_porcentaje);
    descuentoEurosUnitario = precioUnitario * (descuentoPorcentaje / 100);
    precioConDescuento = precioUnitario - descuentoEurosUnitario;
  } else if (batch.descuento_euros && batch.descuento_euros > 0) {
    descuentoEurosUnitario = parseFloat(batch.descuento_euros);
    descuentoPorcentaje = (descuentoEurosUnitario / precioUnitario) * 100;
    precioConDescuento = precioUnitario - descuentoEurosUnitario;
  }
  
  const descuentoTotalEuros = descuentoEurosUnitario * unidades;
  const tieneDescuento = descuentoPorcentaje > 0;
  
  const subtotal = precioConDescuento * unidades;
  const ivaPercent = parseInt(ivaRate) / 100;
  const ivaAmount = subtotal * ivaPercent;
  const total = subtotal + ivaAmount;
  
  // Fecha de la factura cliente: usar fecha_factura_cliente del lote o fecha actual
  const fechaFactura = batch.fecha_factura_cliente 
    ? new Date(batch.fecha_factura_cliente).toLocaleDateString("es-ES")
    : new Date().toLocaleDateString("es-ES");
  const addressLines = formatClientAddress();

  return (
    <div className="invoice-page">
      {/* Barra de acciones - NO se imprime */}
      <div className="no-print p-4 flex items-center justify-between flex-wrap gap-2 bg-white border-b">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            Volver
          </Button>
          <h1 className="text-xl font-bold text-slate-800">
            Factura Cliente #{invoiceNumber}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Label className="text-sm whitespace-nowrap">IVA:</Label>
            <Select value={ivaRate} onValueChange={setIvaRate}>
              <SelectTrigger className="w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">0%</SelectItem>
                <SelectItem value="10">10%</SelectItem>
                <SelectItem value="21">21%</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" size="sm" onClick={handlePrint}>
            <Printer className="w-4 h-4 mr-1" />
            Imprimir
          </Button>
          <Button variant="outline" size="sm" onClick={handleSavePdf} disabled={savingPdf}>
            {savingPdf ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
            Guardar PDF
          </Button>
          {isAdmin && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleSendEmail} 
              disabled={sendingEmail || !client}
              className="bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100"
              data-testid="send-invoice-email-btn"
            >
              {sendingEmail ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />}
              Enviar por Email
            </Button>
          )}
          {isAdmin && (
            <Button size="sm" onClick={handleCreateInvoice} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
              Crear Factura
            </Button>
          )}
        </div>
      </div>

      {/* Contenido de la factura */}
      <div ref={printRef} className="invoice-content bg-white p-6 max-w-3xl mx-auto print:max-w-full print:p-8">
        
        {/* Encabezado con logo */}
        <div className="flex justify-between items-start mb-4 pb-4 border-b border-slate-200">
          <div className="flex-1">
            {empresaActiva?.logo_url ? (
              <img 
                src={getUploadUrl(empresaActiva.logo_url)} 
                alt={empresaActiva?.nombre}
                className="h-14 w-auto object-contain mb-2"
              />
            ) : (
              <div className="h-14 flex items-center mb-2">
                <span className="text-xl font-bold text-slate-800">{empresaActiva?.nombre || "EMPRESA"}</span>
              </div>
            )}
            <div className="text-xs text-slate-600 leading-relaxed">
              <p className="font-bold">{empresaActiva?.nombre || "EMPRESA"}</p>
              <p>{empresaActiva?.direccion || ""}</p>
              <p className="font-mono">{empresaActiva?.cif || ""}</p>
              <p>{empresaActiva?.email || ""}</p>
            </div>
          </div>
          
          <div className="flex-1 text-right">
            <p className="text-[10px] text-slate-500 italic max-w-[220px] ml-auto leading-relaxed">
              Producto del mar congelado de calidad superior, directamente de los pescadores, 
              fruto de una pesca sostenible y socialmente responsable.
            </p>
          </div>
        </div>

        {/* Datos del cliente - formato completo */}
        <div className="mb-4 p-3 bg-slate-50 rounded-lg">
          <p className="text-[10px] font-bold text-slate-500 uppercase mb-1">Cliente</p>
          <p className="font-bold text-base">{client?.nombre || batch.cliente_nombre || "CLIENTE"}</p>
          {/* Dirección en líneas separadas */}
          {addressLines.length > 0 ? (
            <div className="text-xs text-slate-600 leading-relaxed mt-1">
              {addressLines.map((line, idx) => (
                <p key={idx}>{line}</p>
              ))}
            </div>
          ) : (
            <p className="text-xs text-slate-600 mt-1">{client?.direccion || batch.cliente_direccion || ""}</p>
          )}
          <p className="text-xs text-slate-700 font-mono font-semibold mt-1">
            {client?.identificacion_fiscal || client?.cif || batch.cliente_cif || ""}
          </p>
        </div>

        {/* Número de factura y fechas */}
        <div className="mb-4">
          <h2 className="text-xl font-bold text-slate-800">Factura #{invoiceNumber}</h2>
          <p className="text-xs text-slate-500">Fecha {fechaFactura} | Vencimiento: {fechaFactura}</p>
        </div>

        {/* Tabla de productos */}
        <table className="w-full mb-4 text-xs">
          <thead>
            <tr className="border-b-2 border-slate-300">
              <th className="text-left py-2 font-bold text-slate-600">CONCEPTO</th>
              <th className="text-right py-2 font-bold text-slate-600 w-20">PRECIO</th>
              <th className="text-right py-2 font-bold text-slate-600 w-24">UNIDADES</th>
              <th className="text-right py-2 font-bold text-slate-600 w-24">SUBTOTAL</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-slate-200">
              <td className="py-3 align-top">
                <p className="font-bold text-slate-800 text-sm mb-2">{product?.nombre || batch.producto_nombre || "Producto"}</p>
                <div className="text-[10px] text-slate-500 leading-relaxed space-y-0.5">
                  <p><b>Presentación:</b> {product?.presentacion || batch.presentacion || "Sin visceras, con pico y ojo."}</p>
                  <p><b>Temperatura:</b> Mantener congelado a -18º C</p>
                  <p><b>Zona FAO:</b> {batch.zona_fao || product?.zona_fao || "Zona FAO31"}</p>
                  <p><b>Nombre científico:</b> {product?.nombre_cientifico || batch.nombre_cientifico || "Octopus Vulgaris"}</p>
                  <p><b>Arte de pesca:</b> {batch.arte_pesca || product?.arte_pesca || "Capturado artesanal"}</p>
                  <p><b>Código Alfa:</b> {product?.codigo_alfa || "OCC"}</p>
                  <p><b>Puerto desembarco:</b> {product?.puerto_desembarco || "Nueva Esparta, Isla de Margarita"}</p>
                  <p><b>País origen:</b> {product?.pais_origen || batch.pais_origen || "Venezuela"}</p>
                  <p><b>Reg. sanitario importador:</b> {empresaActiva?.rgseaa || ""}</p>
                  <p><b>Fecha captura:</b> {formatDate(batch.fecha_captura)} | <b>Caducidad:</b> {formatDate(batch.fecha_caducidad)}</p>
                  <p className="font-bold mt-1 text-sm bg-slate-100 p-1 rounded"><b>LOTE:</b> {batch.numero_lote} {batch.factura_origen && `| F.Origen: ${batch.factura_origen}`} {batch.contenedor && `| Cont: ${batch.contenedor}`}{batch.awb_aereo && `| AWB: ${batch.awb_aereo}`}</p>
                </div>
              </td>
              <td className="py-3 text-right align-top font-mono">
                {tieneDescuento ? (
                  <div>
                    <span className="line-through text-slate-400">{formatNumber(precioUnitario)}€</span>
                    <br />
                    <span className="text-emerald-700 font-bold">{formatNumber(precioConDescuento)}€</span>
                  </div>
                ) : (
                  <span>{formatNumber(precioUnitario)}€</span>
                )}
              </td>
              <td className="py-3 text-right align-top font-mono">{formatNumber(unidades, 3)}</td>
              <td className="py-3 text-right align-top font-mono font-bold">{formatNumber(subtotal)}€</td>
            </tr>
          </tbody>
        </table>

        {/* Totales */}
        <div className="mb-6">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-300">
                {tieneDescuento && (
                  <>
                    <th className="text-left py-2 font-bold text-slate-500">DESCUENTO</th>
                    <th className="text-left py-2 font-bold text-slate-500">DESC. EUROS</th>
                  </>
                )}
                <th className="text-left py-2 font-bold text-slate-500">BASE IMPONIBLE</th>
                <th className="text-center py-2 font-bold text-slate-500">IMPUESTO</th>
                <th className="text-right py-2 font-bold text-slate-500">TOTAL IMPUESTO</th>
                <th className="text-right py-2 font-bold text-slate-500 w-28">TOTAL</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-slate-200">
                {tieneDescuento && (
                  <>
                    <td className="py-2 font-mono">{descuentoPorcentaje.toFixed(4)}%</td>
                    <td className="py-2 font-mono text-red-600">-{formatNumber(descuentoTotalEuros)}€</td>
                  </>
                )}
                <td className="py-2 font-mono">{formatNumber(subtotal)}€</td>
                <td className="py-2 text-center">IVA {ivaRate}%</td>
                <td className="py-2 text-right font-mono">{formatNumber(ivaAmount)}€</td>
                <td className="py-2 text-right font-mono font-bold">{formatNumber(total)}€</td>
              </tr>
              <tr>
                <td colSpan={tieneDescuento ? "5" : "3"} className="py-2 text-right font-bold text-base">TOTAL</td>
                <td className="py-2 text-right font-mono font-bold text-base text-emerald-700">{formatNumber(total)}€</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Pie de página */}
        <div className="border-t border-slate-300 pt-3 text-center">
          {empresaActiva?.cuenta_bancaria && (
            <p className="text-xs text-slate-600 mb-2">
              Pagar por transferencia bancaria: <span className="font-mono font-bold">{empresaActiva.cuenta_bancaria}</span>
            </p>
          )}
          <p className="text-[10px] text-slate-400">
            {empresaActiva?.nombre || "EMPRESA"} {empresaActiva?.cif || ""}{empresaActiva?.registro_mercantil ? `. ${empresaActiva.registro_mercantil}` : ""}
          </p>
        </div>
      </div>

      {/* Estilos para impresión */}
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 10mm;
          }
          
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          /* Ocultar Made with Emergent y otros elementos no deseados */
          #emergent-badge,
          .no-print,
          .print-hide,
          [data-testid="app-sidebar"],
          .sidebar,
          aside,
          nav,
          header {
            display: none !important;
          }
          
          /* Mostrar contenido de la factura */
          .invoice-page {
            display: block !important;
          }
          
          .invoice-content {
            display: block !important;
            max-width: 100% !important;
            width: 100% !important;
            padding: 0 !important;
            margin: 0 !important;
            box-shadow: none !important;
          }
          
          /* Asegurar visibilidad de todos los elementos */
          .invoice-content * {
            visibility: visible !important;
          }
        }
        
        @media screen {
          .invoice-content {
            box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06);
            border-radius: 8px;
            margin-top: 1rem;
            margin-bottom: 1rem;
          }
        }
      `}</style>
    </div>
  );
}
